import React, { useCallback, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { bubblePath } from '../utils/bubblePath';
import type { ChatOption } from '../types/chat';
interface ReplyChipProps {
  option: ChatOption;
  index: number;
  count: number;
  /** This chip is the answer the visitor just sent */
  picked: boolean;
  disabled: boolean;
  reduce: boolean;
  onChoose: (option: ChatOption) => void;
  /** iMessage tail at the bottom-right corner (e.g. pointing at Pixel) */
  tail?: boolean;
}
const EASE = [0.23, 1, 0.32, 1] as const;
/** Entrance pop */
const POP = {
  type: 'spring',
  stiffness: 420,
  damping: 24,
  mass: 0.7
} as const;
/** Hover in/out: short, plain ease-out — no overshoot */
const HOVER = {
  duration: 0.18,
  ease: EASE
} as const;
/** Press */
const TAP = {
  duration: 0.1,
  ease: EASE
} as const;

/**
 * A reply suggestion. Blue from the start; the one you send stays and grows slightly,
 * the rest drop down and vanish.
 */
export function ReplyChip({
  option,
  index,
  picked,
  disabled,
  reduce,
  onChoose,
  tail = false
}: ReplyChipProps) {
  // The stagger delay only applies to the first pop-in — never to hover or hover-out
  const [entered, setEntered] = useState(false);
  const interactive = !reduce && !disabled;
  // Size of the chip, for drawing the tailed outline
  const [box, setBox] = useState({
    w: 0,
    h: 0
  });
  const observer = useRef<ResizeObserver | null>(null);
  const measureRef = useCallback((el: HTMLButtonElement | null) => {
    observer.current?.disconnect();
    if (!el || !tail) return;
    const measure = () => setBox({
      w: el.offsetWidth,
      h: el.offsetHeight
    });
    observer.current = new ResizeObserver(measure);
    observer.current.observe(el);
    measure();
  }, [tail]);
  return <motion.button ref={measureRef} type="button" layout={!reduce} disabled={disabled} onClick={() => onChoose(option)} initial={reduce ? false : {
    opacity: 0,
    scale: 0.6,
    y: 10
  }} animate={{
    opacity: 1,
    scale: picked ? 1.04 : 1,
    y: 0,
    transition: reduce ? {
      duration: 0
    } : entered || picked ? HOVER : {
      ...POP,
      delay: 0.05 + index * 0.11
    }
  }} onAnimationComplete={() => setEntered(true)}
  // Lost options: a quick little drop and fade
  exit={reduce ? {
    opacity: 0,
    transition: {
      duration: 0
    }
  } : {
    opacity: 0,
    y: 16,
    scale: 0.96,
    transition: {
      duration: 0.18,
      ease: EASE,
      delay: index * 0.02
    }
  }} whileHover={interactive ? {
    scale: 1.03,
    transition: HOVER
  } : undefined} whileTap={interactive ? {
    scale: 0.97,
    transition: TAP
  } : undefined} className="group pointer-events-auto relative origin-right whitespace-nowrap rounded-full px-[0.95em] py-[0.42em] text-[clamp(0.85rem,calc(1*var(--vw)),1.125rem)] font-medium tracking-[-0.015em] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1088F0] disabled:cursor-default" data-id="element-259" data-mp="2v9rrck0mqj7p">
      {/* Blue from the start; slightly deeper on hover */}
      <span aria-hidden="true" className="absolute inset-0 rounded-full bg-[#1088F0] transition-colors duration-150 ease-out group-hover:bg-[#0A72D1] group-disabled:bg-[#1088F0]" data-id="element-260" data-mp="1gfnw5xr66aab" />
      {tail && box.w > 0 &&
    // Body + tail as ONE continuous iMessage outline (mirrored so the tail curls out bottom-right),
    // drawn over the plain pill so the join is seamless; follows the hover colour
    <svg aria-hidden="true" width={box.w} height={box.h} viewBox={`0 0 ${box.w} ${box.h}`} className="pointer-events-none absolute left-0 top-0 -scale-x-100 overflow-visible text-[#1088F0] transition-colors duration-150 ease-out group-hover:text-[#0A72D1] group-disabled:text-[#1088F0]" data-id="element-261" data-mp="201smdvedjrc6">
          <path d={bubblePath(box.w, box.h, box.h / 2, true)} fill="currentColor" data-id="element-262" data-mp="2itlja0wkdwp6" />
        </svg>}
      <span className="relative text-white" data-id="element-263" data-mp="u7isai68ud9g">{option.label}</span>
    </motion.button>;
}